import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, MapPin, Calendar, Trash2, ChevronRight, Plane, Download, FileSpreadsheet, Star } from 'lucide-react';
import { useTravelItineraries, TravelItineraryWithStops } from '@/hooks/useTravelItineraries';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { calculateMonthData, DayData, getVisibilityRating } from '@/utils/calendarData';
import { writeExcelFile, type ExcelSheet } from '@/utils/excelExport';
import { toast } from 'sonner';

interface TripListViewProps {
  onSelectTrip: (trip: TravelItineraryWithStops) => void;
}

export const TripListView: React.FC<TripListViewProps> = ({ onSelectTrip }) => {
  const { itineraries, loading, createItinerary, deleteItinerary } = useTravelItineraries();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTripName, setNewTripName] = useState('');
  const [newTripNotes, setNewTripNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTrip, setDeleteTrip] = useState<TravelItineraryWithStops | null>(null);

  const handleCreateTrip = async () => {
    if (!newTripName.trim()) return;
    
    setIsCreating(true);
    const result = await createItinerary(newTripName.trim(), newTripNotes.trim() || undefined);
    setIsCreating(false);
    
    if (result) {
      setIsCreateOpen(false);
      setNewTripName('');
      setNewTripNotes('');
    }
  };

  const handleDeleteTrip = async () => {
    if (!deleteTrip) return;
    await deleteItinerary(deleteTrip.id);
    setDeleteTrip(null);
  };

  const getTripDateRange = (trip: TravelItineraryWithStops) => {
    if (trip.stops.length === 0) return null;
    
    const dates = trip.stops.flatMap(stop => [
      parseISO(stop.arrival_date),
      parseISO(stop.departure_date)
    ]);
    
    const earliest = new Date(Math.min(...dates.map(d => d.getTime())));
    const latest = new Date(Math.max(...dates.map(d => d.getTime())));
    
    return {
      start: format(earliest, 'MMM d'),
      end: format(latest, 'MMM d, yyyy')
    };
  };

  const createVisibilityBars = (rating: number): string => {
    const filledBars = '█'.repeat(rating);
    const emptyBars = '░'.repeat(10 - rating);
    return filledBars + emptyBars;
  };

  const getMoonPhaseName = (phase: number): string => {
    const moonPercent = Math.round(phase * 100);
    if (moonPercent <= 5) return 'New Moon';
    if (moonPercent <= 25) return 'Waxing Crescent';
    if (moonPercent <= 45) return 'First Quarter';
    if (moonPercent <= 55) return 'Waxing Gibbous';
    if (moonPercent <= 95) return 'Full Moon';
    return 'Waning';
  };

  const calculateNightData = (stop: TravelItineraryWithStops['stops'][0]) => {
    const arrival = parseISO(stop.arrival_date);
    const departure = parseISO(stop.departure_date);
    const lastNight = subDays(departure, 1);
    const nights = arrival <= lastNight ? eachDayOfInterval({ start: arrival, end: lastNight }) : [];

    const location = {
      name: stop.location_name,
      latitude: Number(stop.latitude),
      longitude: Number(stop.longitude)
    };

    // Pre-compute unique months to avoid redundant calls
    const monthCache = new Map<string, DayData[]>();
    nights.forEach(night => {
      const key = `${night.getMonth()}-${night.getFullYear()}`;
      if (!monthCache.has(key)) {
        monthCache.set(key, calculateMonthData(location, night.getMonth(), night.getFullYear()));
      }
    });

    return nights.map(night => {
      const key = `${night.getMonth()}-${night.getFullYear()}`;
      const monthData = monthCache.get(key) || [];
      const dayData = monthData.find(d => d.date === night.getDate());
      return {
        date: format(night, 'yyyy-MM-dd'),
        displayDate: format(night, 'MMM d, yyyy'),
        ...dayData
      };
    }).filter(d => d.moonPhase !== undefined);
  };

  // Compute trip summaries asynchronously so the list renders immediately
  const [tripSummaries, setTripSummaries] = useState<Record<string, { totalNights: number; goodNights: number; bestRating: number; bestNightDate: string }>>({});

  useEffect(() => {
    if (loading || itineraries.length === 0) return;

    // Compute all trip summaries in parallel
    const computeSummaries = async () => {
      const entries = await Promise.all(
        itineraries
          .filter(trip => trip.stops.length > 0)
          .map(async (trip) => {
            // Yield to browser before each trip's computation
            await new Promise(resolve => setTimeout(resolve, 0));

            let totalNights = 0;
            let goodNights = 0;
            let bestRating = 0;
            let bestNightDate = '';

            trip.stops.forEach(stop => {
              const nightData = calculateNightData(stop);
              totalNights += nightData.length;
              nightData.forEach(night => {
                const rating = getVisibilityRating(night as DayData);
                if (rating >= 7) goodNights++;
                if (rating > bestRating) {
                  bestRating = rating;
                  bestNightDate = night.displayDate;
                }
              });
            });

            return [trip.id, { totalNights, goodNights, bestRating, bestNightDate }] as const;
          })
      );

      const summaries: typeof tripSummaries = {};
      entries.forEach(([id, summary]) => { summaries[id] = summary; });
      setTripSummaries(summaries);
    };

    computeSummaries();
  }, [itineraries, loading]);

  const getTripSummary = (trip: TravelItineraryWithStops) => {
    return tripSummaries[trip.id] || null;
  };

  const exportTripToExcel = async (trip: TravelItineraryWithStops, e: React.MouseEvent) => {
    e.stopPropagation();

    if (trip.stops.length === 0) {
      toast.error('No stops to export');
      return;
    }

    const sheets: ExcelSheet[] = [];
    const dateRange = getTripDateRange(trip);
    const summary = getTripSummary(trip);

    // Sheet 1: Trip Overview
    const overviewData = [
      ['TRAVEL ITINERARY EXPORT'],
      [''],
      ['Trip Name:', trip.name],
      ['Date Range:', dateRange ? `${dateRange.start} - ${dateRange.end}` : 'No dates'],
      ['Total Stops:', trip.stops.length],
      ['Total Nights:', summary?.totalNights || 0],
      ['Excellent Nights (7+/10):', summary?.goodNights || 0],
      [''],
      ['HOW TO USE THIS DATA'],
      ['• Rating shown as bars: ██████████ = 10/10 (perfect), ░░░░░░░░░░ = 0/10 (not visible)'],
      ['• 7+ bars: Excellent conditions for Milky Way photography'],
      ['• 4-6 bars: Moderate conditions, some moon interference'],
      ['• 0-3 bars: Poor conditions or galactic core not visible']
    ];
    sheets.push({ name: 'Trip Overview', data: overviewData });

    // Sheet 2: Itinerary Summary
    const itineraryHeaders = ['Stop #', 'Location', 'Arrival', 'Departure', 'Nights', 'Best Night', 'Best Rating', 'Latitude', 'Longitude', 'Notes'];
    const itineraryRows = trip.stops
      .sort((a, b) => a.stop_order - b.stop_order)
      .map((stop, idx) => {
        const nightData = calculateNightData(stop);
        const bestNight = nightData.reduce((best, night) => {
          const rating = getVisibilityRating(night as DayData);
          return rating > (best?.rating || 0) ? { ...night, rating } : best;
        }, null as (typeof nightData[number] & { rating: number }) | null);

        return [
          idx + 1,
          stop.location_name,
          format(parseISO(stop.arrival_date), 'MMM d, yyyy'),
          format(parseISO(stop.departure_date), 'MMM d, yyyy'),
          nightData.length,
          bestNight?.displayDate || 'N/A',
          bestNight?.rating ? createVisibilityBars(bestNight.rating) : 'N/A',
          Number(stop.latitude).toFixed(4),
          Number(stop.longitude).toFixed(4),
          stop.notes || ''
        ];
      });
    sheets.push({ name: 'Itinerary', data: [itineraryHeaders, ...itineraryRows] });

    // Sheet 3+: Detailed per-stop data
    trip.stops
      .sort((a, b) => a.stop_order - b.stop_order)
      .forEach((stop, idx) => {
        const nightData = calculateNightData(stop);
        const headers = ['Date', 'Sunrise', 'Sunset', 'Core Start', 'Core End', 'Moon %', 'Moon Phase', 'Visibility'];
        const rows = nightData.map(night => {
          const rating = getVisibilityRating(night as DayData);
          return [
            night.displayDate,
            night.sunrise || '--',
            night.sunset || '--',
            night.galacticCoreStart || '--',
            night.galacticCoreEnd || '--',
            night.moonPhase ? `${Math.round(night.moonPhase * 100)}%` : '--',
            night.moonPhase ? getMoonPhaseName(night.moonPhase) : '--',
            createVisibilityBars(rating)
          ];
        });

        const sheetName = `${idx + 1} - ${stop.location_name}`.substring(0, 31);
        sheets.push({ name: sheetName, data: [headers, ...rows] });
      });

    const safeName = trip.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    const filename = `Trip_${safeName}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    await writeExcelFile(sheets, filename);
    toast.success(`Exported ${filename}`);
  };

  const exportTripToCSV = (trip: TravelItineraryWithStops, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (trip.stops.length === 0) {
      toast.error('No stops to export');
      return;
    }

    const headers = ['Stop #', 'Location', 'Date', 'Core Start', 'Core End', 'Moon %', 'Rating', 'Notes'];
    const rows: string[][] = [];

    trip.stops
      .sort((a, b) => a.stop_order - b.stop_order)
      .forEach((stop, idx) => {
        const nightData = calculateNightData(stop);
        nightData.forEach(night => {
          const rating = getVisibilityRating(night as DayData);
          rows.push([
            String(idx + 1),
            stop.location_name,
            night.displayDate,
            night.galacticCoreStart || '--',
            night.galacticCoreEnd || '--',
            night.moonPhase ? `${Math.round(night.moonPhase * 100)}%` : '--',
            String(rating),
            stop.notes || ''
          ]);
        });
      });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = trip.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    link.download = `Trip_${safeName}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Exported CSV');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Your Trips</h2>
          <p className="text-muted-foreground">Plan multi-location astrophotography trips</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              New Trip
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Trip</DialogTitle>
              <DialogDescription>
                Give your trip a name to get started. You can add locations after.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="trip-name">Trip Name</Label>
                <Input
                  id="trip-name"
                  placeholder="e.g., Southwest Astrophotography Tour"
                  value={newTripName}
                  onChange={(e) => setNewTripName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trip-notes">Notes (optional)</Label>
                <Textarea
                  id="trip-notes"
                  placeholder="Any notes about this trip..."
                  value={newTripNotes}
                  onChange={(e) => setNewTripNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTrip} disabled={!newTripName.trim() || isCreating}>
                {isCreating ? 'Creating...' : 'Create Trip'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Trip List */}
      {itineraries.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Plane className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No trips yet</h3>
            <p className="text-muted-foreground mb-4 max-w-sm">
              Create your first trip to plan Milky Way visibility across multiple locations
            </p>
            <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Your First Trip
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {itineraries.map((trip) => {
            const dateRange = getTripDateRange(trip);
            const summary = getTripSummary(trip);
            return (
              <Card 
                key={trip.id} 
                className="hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => onSelectTrip(trip)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {trip.name}
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </CardTitle>
                      {trip.notes && (
                        <CardDescription className="mt-1 line-clamp-2">
                          {trip.notes}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {trip.stops.length > 0 && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary"
                            onClick={(e) => exportTripToExcel(trip, e)}
                            title="Export to Excel"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary"
                            onClick={(e) => exportTripToCSV(trip, e)}
                            title="Export to CSV"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTrip(trip);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{trip.stops.length} {trip.stops.length === 1 ? 'stop' : 'stops'}</span>
                    </div>
                    {dateRange && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{dateRange.start} - {dateRange.end}</span>
                      </div>
                    )}
                    {summary && (
                      <>
                        <div className="flex items-center gap-1">
                          <span>{summary.totalNights} nights</span>
                        </div>
                        <div className="flex items-center gap-1 text-primary">
                          <Star className="w-4 h-4 fill-primary" />
                          <span>{summary.goodNights} excellent</span>
                        </div>
                      </>
                    )}
                  </div>
                  {trip.stops.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {trip.stops.slice(0, 4).map((stop, index) => (
                        <span 
                          key={stop.id}
                          className="text-xs bg-muted px-2 py-1 rounded-full"
                        >
                          {index + 1}. {stop.location_name}
                        </span>
                      ))}
                      {trip.stops.length > 4 && (
                        <span className="text-xs text-muted-foreground px-2 py-1">
                          +{trip.stops.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTrip} onOpenChange={() => setDeleteTrip(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trip</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTrip?.name}"? This will also delete all stops in this trip. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTrip} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
